/**
 * Google Analytics 4 helpers — Indiana Accident Aid.
 *
 * The GA4 Measurement ID is configured via env: REACT_APP_GA_MEASUREMENT_ID.
 * If it is unset, analytics is a safe no-op (nothing is loaded or sent), so a
 * missing/unverified property never blocks launch. Call initAnalytics() once at
 * app mount; it dynamically loads gtag only when an ID is present and disables
 * automatic page_view so SPA route changes are tracked accurately.
 */

const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || "";

export function initAnalytics() {
  if (!GA_MEASUREMENT_ID || typeof window === "undefined") return;
  if (window.gtag) return; // already initialised
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;
  gtag("js", new Date());
  gtag("config", GA_MEASUREMENT_ID, { send_page_view: false });
}

function _hasGtag() {
  return !!GA_MEASUREMENT_ID && typeof window !== "undefined" && typeof window.gtag === "function";
}

/** Fire a GA4 pageview. Pass the current pathname + search. */
export function trackPageview(path, title) {
  if (!_hasGtag()) return;
  window.gtag("event", "page_view", {
    page_path: path,
    page_title: title || (typeof document !== "undefined" ? document.title : ""),
    page_location: typeof window !== "undefined" ? window.location.href : "",
    send_to: GA_MEASUREMENT_ID,
  });
}

/** Fire a custom GA4 event with the given name + params. */
export function trackEvent(eventName, params = {}) {
  if (!_hasGtag()) return;
  window.gtag("event", eventName, { ...params, send_to: GA_MEASUREMENT_ID });
}

// --- Named convenience helpers for the four required custom events ---

export const trackContactFormStarted = (formId = "lead_form") =>
  trackEvent("contact_form_started", { form_id: formId });

export const trackContactFormSubmitted = (formId = "lead_form", extra = {}) =>
  trackEvent("contact_form_submitted", { form_id: formId, ...extra });

export const trackClickToCall = (phone, source = "site") =>
  trackEvent("click_to_call", { phone_number: phone, source });

export const trackAttorneyContactClicked = (source = "attorney_card", extra = {}) =>
  trackEvent("attorney_contact_clicked", { source, ...extra });
